/**
 * Error boundary for analytics-v2 widgets (CORE-1041).
 *
 * Isolates blast radius: a render-time throw inside a widget renders a
 * title-scoped fallback instead of blanking the whole AnalyticsTab
 * (sibling widgets + legacy GA4 sections would otherwise disappear).
 *
 * **Recovery**: the boundary resets its `hasError` state when the
 * `resetKey` prop changes — widgets pass their `selectedWindow` so that
 * a user action (picking a different window) re-attempts the render
 * with fresh data instead of staying in the sticky fallback until a
 * page reload.
 *
 * React ErrorBoundary contract (documented limitations):
 *   - Catches: errors in render + lifecycle methods of children.
 *   - Does NOT catch: async errors, event-handler errors, setState
 *     inside useEffect. Those get logged by the async code itself
 *     (`formatErrorMessage` in the fetch hook, catch-blocks in event
 *     handlers).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  title: string;
  children: ReactNode;
  /**
   * When this value changes, the boundary resets its error state and
   * re-attempts to render children. Widgets pass their `selectedWindow`
   * (or another user-controllable key) so a control change is a natural
   * retry signal.
   *
   * Required — omitting it would leave the boundary permanently sticky
   * after any crash (page reload the only recovery), which is a silent
   * footgun for widget authors. If a widget genuinely has no retry
   * signal, pass a constant literal to make the "no retry" intent
   * explicit.
   */
  resetKey: string | number;
}

interface State {
  hasError: boolean;
  prevResetKey: string | number | undefined;
}

export class AnalyticsV2ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, prevResetKey: undefined };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // Reset the error flag whenever resetKey changes, allowing the boundary
    // to re-attempt rendering children. Track prevResetKey so unrelated
    // rerenders (parent state changes) do NOT reset — that would let the
    // boundary re-trip on every paint after a throw.
    if (props.resetKey !== state.prevResetKey) {
      return { hasError: false, prevResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Load-bearing: silent widget crashes are worse than loud ones — the
    // operator sees the fallback, and the underlying error must still land
    // in the browser console for triage.
    // eslint-disable-next-line no-console -- ErrorBoundary triage log
    console.error(
      `[AnalyticsV2ErrorBoundary] "${this.props.title}" widget crashed:`,
      error,
      errorInfo,
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="analytics-v2-widget-boundary-error" role="alert">
          {this.props.title} widget could not render. Check the browser console for details.
        </div>
      );
    }
    return this.props.children;
  }
}
